import { messaging, isMessagingSupported } from './firebaseConfig';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { supabaseDataService } from './supabaseService';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

class NotificationService {
    constructor() {
        this.token = null;
        this.isSupported = false;
        this.isInitialized = false;
        this.initializationPromise = null;
        this.inAppNotificationCallbacks = new Set();

        // Start initialization immediately but don't block constructor
        this.initializationPromise = this.init();
    }

    async init() {
        try {
            this.isSupported = await isMessagingSupported();
            console.log('\n\n\n******* -this.isSupported---->', this.isSupported);
            if (this.isSupported) {
                await this.setupForegroundListener();
                this.setupServiceWorkerMessageListener();
            }
        } catch (error) {
            console.error('Error initializing notifications:', error);
            this.isSupported = false;
        } finally {
            this.isInitialized = true;
        }
    }

    // Listen for messages from service worker (for background notifications forwarded to the app)
    setupServiceWorkerMessageListener() {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                console.log('[ServiceWorker Message]', event.data);
                if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
                    const { title, body, icon, data } = event.data.payload;
                    this.triggerInAppNotification({
                        title,
                        body,
                        icon,
                        data,
                        timestamp: new Date().toISOString()
                    });
                }
            });
            console.log('[setupServiceWorkerMessageListener] Listening for service worker messages');
        }
    }

    // Ensure initialization is complete before performing operations
    async ensureInitialized() {
        if (!this.isInitialized) {
            await this.initializationPromise;
        }
        return this.isInitialized;
    }

    // Add method to register in-app notification callbacks
    addInAppNotificationCallback(callback) {
        this.inAppNotificationCallbacks.add(callback);
        return () => this.inAppNotificationCallbacks.delete(callback);
    }

    // Trigger in-app notifications
    triggerInAppNotification(notification) {
        this.inAppNotificationCallbacks.forEach(callback => {
            try {
                callback(notification);
            } catch (error) {
                console.error('Error in in-app notification callback:', error);
            }
        });
    }

    async requestPermission() {
        console.log('[requestPermission]-----1>');
        await this.ensureInitialized();

        console.log('[requestPermission]-----2>');
        if (!this.isSupported) {
            throw new Error('Notifications not supported in this browser');
        }

        console.log('[requestPermission]-----3>');
        const permission = await Notification.requestPermission();

        console.log('[requestPermission]-----4permission>',permission);
        if (permission === 'granted') {
            console.log('Notification permission granted');
            return await this.getToken();
        } else {
            throw new Error('Notification permission denied');
        }
    }

    async getToken() {
        console.log('[getToken]-----1>');
        await this.ensureInitialized();

        console.log('[getToken]-----2>');
        if (!this.isSupported || !messaging) return null;

        try {
            console.log('[getToken]-----3VAPID_KEY>', VAPID_KEY);

            // Get the service worker registration to pass to Firebase
            const swRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
            console.log('[getToken]-----3.5 swRegistration>', swRegistration);

            const currentToken = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: swRegistration
            });

            console.log('[getToken]-----4currentToken>', currentToken);
            if (currentToken) {
                console.log('FCM Token:', currentToken);
                this.token = currentToken;
                return currentToken;
            } else {
                console.log('No registration token available');
                return null;
            }
        } catch (error) {
            console.error('An error occurred while retrieving token:', error);
            return null;
        }
    }

    // New method to handle token cleanup when disabling notifications
    async clearToken() {
        if (!this.isSupported || !messaging) return;

        try {
            // Delete the FCM token
            await deleteToken(messaging);
            this.token = null;
            console.log('FCM token deleted successfully');
        } catch (error) {
            console.error('Error deleting FCM token:', error);
            // Still clear the local token even if deletion fails
            this.token = null;
        }
    }

    async setupForegroundListener() {
        if (!this.isSupported || !messaging) {
            console.log('[setupForegroundListener] Not supported or no messaging');
            return;
        }

        console.log('[setupForegroundListener] Setting up foreground message listener');

        onMessage(messaging, (payload) => {
            console.log('*** FOREGROUND message received:', payload);

            const notificationData = {
                title: payload.notification?.title || 'New Notification',
                body: payload.notification?.body || '',
                icon: payload.notification?.icon || '/pwa-192x192.png',
                data: payload.data || {},
                timestamp: new Date().toISOString()
            };

            // Always trigger in-app notification
            this.triggerInAppNotification(notificationData);

            // Try to show browser notification if permission is granted
            if (Notification.permission === 'granted') {
                try {
                    const browserNotification = new Notification(notificationData.title, {
                        body: notificationData.body,
                        icon: notificationData.icon,
                        tag: payload.data?.tag || 'default',
                        data: payload.data,
                        requireInteraction: true, // Keep notification visible until user interacts
                        silent: false // Allow notification sound
                    });

                    browserNotification.onclick = (event) => {
                        event.preventDefault();
                        window.focus();

                        // Navigate based on notification data
                        if (payload.data?.url) {
                            window.location.href = payload.data.url;
                        }

                        browserNotification.close();
                    };

                    // Auto-close after 10 seconds
                    setTimeout(() => {
                        browserNotification.close();
                    }, 10000);

                } catch (error) {
                    console.error('Error showing browser notification:', error);
                    // Browser notification failed, but in-app notification is still triggered above
                }
            } else {
                console.log('Browser notification permission not granted, showing in-app only');
            }
        });
    }

    async saveTokenToDatabase(uid, token, groupID) {
        try {
            // Save token to your database
            await supabaseDataService.saveNotificationToken(uid, token, groupID);
        } catch (error) {
            console.error('Error saving token to database:', error);
        }
    }

    async removeTokenFromDatabase(uid, token) {
        try {
            await supabaseDataService.removeTokenFromDatabase(uid, token);
        } catch (error) {
            console.error('Error removing token from database:', error);
        }
    }

    getPermissionStatus() {
        if (!('Notification' in window)) {
            return 'not-supported';
        }
        return Notification.permission;
    }

    isPermissionGranted() {
        return this.getPermissionStatus() === 'granted';
    }

    // Debug method to test notifications
    testNotification() {
        const testData = {
            title: 'Test Notification',
            body: 'This is a test notification from the app',
            icon: '/icon-192x192.png',
            data: { test: true },
            timestamp: new Date().toISOString()
        };

        this.triggerInAppNotification(testData);

        if (Notification.permission === 'granted') {
            try {
                new Notification(testData.title, {
                    body: testData.body,
                    icon: testData.icon
                });
            } catch (error) {
                console.error('Error showing test browser notification:', error);
            }
        }
    }
}

export const notificationService = new NotificationService();